import { HStack, Icon, TextareaBox, VStack } from "@/shared/ui/primitives";
import type { RefObject } from "react";
import type { ChatMessage } from "../update.utils";

type UpdateAssistantPanelProps = {
	messages: ChatMessage[];
	currentAssistantMessage: string;
	isStreaming: boolean;
	input: string;
	inputPlaceholder: string;
	onInputChange: (value: string) => void;
	onSend: () => void;
	textareaRef: RefObject<HTMLTextAreaElement | null>;
	messagesEndRef: RefObject<HTMLDivElement | null>;
};

export const UpdateAssistantPanel = ({
	messages,
	currentAssistantMessage,
	isStreaming,
	input,
	inputPlaceholder,
	onInputChange,
	onSend,
	textareaRef,
	messagesEndRef,
}: UpdateAssistantPanelProps) => {
	const isSendDisabled = !input.trim() || isStreaming;

	return (
		<VStack className="w-full h-full max-w-[360px] p-[16px] gap-[6px] rounded-[16px] outline-outline">
			<VStack className="w-full h-full overflow-auto justify-start items-end gap-[20px] text-sm font-normal">
				{messages.map((msg, i) =>
					msg.role === "user" ? (
						<span key={i} className="p-[10px] rounded-[10px] bg-bg-regular text-ct-strong">
							{msg.content}
						</span>
					) : (
						<span key={i} className="w-full text-ct-regular whitespace-pre-wrap">
							{msg.content}
						</span>
					),
				)}
				{currentAssistantMessage ? (
					<span className="w-full text-ct-regular whitespace-pre-wrap">
						{currentAssistantMessage}
					</span>
				) : null}
				{isStreaming && !currentAssistantMessage ? (
					<HStack className="w-full gap-[4px] py-[10px] justify-start">
						<span
							className="w-[4px] h-[4px] rounded-full bg-ct-weak animate-bounce-dot"
							style={{ animationDelay: "0ms" }}
						/>
						<span
							className="w-[4px] h-[4px] rounded-full bg-ct-weak animate-bounce-dot"
							style={{ animationDelay: "150ms" }}
						/>
						<span
							className="w-[4px] h-[4px] rounded-full bg-ct-weak animate-bounce-dot"
							style={{ animationDelay: "300ms" }}
						/>
					</HStack>
				) : null}
				<div ref={messagesEndRef} />
			</VStack>
			<HStack className="w-full gap-[10px] shrink-0 items-end">
				<TextareaBox
					ref={textareaRef}
					placeholder={inputPlaceholder}
					className="w-full max-h-[120px] overflow-y-auto"
					rows={1}
					value={input}
					onChange={(e) => onInputChange(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter" && !e.nativeEvent.isComposing) {
							onSend();
						}
					}}
					disabled={isStreaming}
				/>
				<button
					type="button"
					className={`aspect-square h-[48px] rounded-[10px] shrink-0 ${
						isSendDisabled ? "bg-bg-regular text-ct-regular" : "bg-pm-strong text-ct-elevated"
					}`}
					onClick={onSend}
					disabled={isSendDisabled}
				>
					<Icon name="Send" />
				</button>
			</HStack>
		</VStack>
	);
};
